import { useQuery } from '@tanstack/react-query';
import { BILLING_MODE_LABELS, MODULE_LABELS, PLAN_LABELS, type BillingStatsDTO, type ModuleKey, type Plan } from '@bifeai/shared';
import { BarChart, Badge, Card, CardBody, CardHeader, EmptyState, Kpi, PageHeader, Skeleton } from '@bifeai/ui';
import { CalendarClock, CircleDollarSign, Repeat, TrendingDown, TrendingUp, UserMinus } from 'lucide-react';
import { DateRangePicker, useDateRange } from '../components/DateRange';
import { api } from '../lib/api';
import { formatDate, formatUsd } from '../lib/format';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const SERIES = [
  { key: 'licenseCents', label: 'Licencias nuevas', color: 'var(--bf-azul, #0E2A47)' },
  { key: 'renewalCents', label: 'Mantenimientos', color: 'var(--bf-celeste, #2E9BDA)' },
  { key: 'subscriptionCents', label: 'Abonos mensuales', color: 'var(--bf-amber, #E8A33D)' },
  { key: 'expansionCents', label: 'Ventas a clientes actuales', color: 'var(--bf-green, #22A06B)' },
] as const;
const pct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 100)}%`);

/** Estadísticas de los dos modelos de cobro: licencia (pago único + mantenimiento anual) y abono mensual. */
export function StatsPage() {
  const range = useDateRange('year');
  const qs = range.qs() || 'all=1';
  const s = useQuery({ queryKey: ['billing', 'stats', qs], queryFn: () => api.get<BillingStatsDTO>(`/platform/billing/stats?${qs}`) });
  const header = (
    <PageHeader
      title="Estadísticas"
      subtitle="Licencia y abono mensual: lo que entra seguro cada mes, lo que se vende, lo que hay que cobrar y los clientes que se van."
      actions={<DateRangePicker r={range} />}
    />
  );
  if (s.isPending || !s.data) {
    return (
      <>
        {header}
        <Skeleton style={{ height: 320 }} />
      </>
    );
  }
  const d = s.data;
  const period = range.label.toLowerCase();
  const sum = (b: (typeof d.byMonth)[number] | (typeof d.byYear)[number] | BillingStatsDTO['periodTotals']) =>
    b.licenseCents + b.renewalCents + b.subscriptionCents + b.expansionCents;
  const periodTotal = sum(d.periodTotals);
  const multiYear = new Set(d.byMonth.map((m) => m.month.slice(0, 4))).size > 1;
  const monthLabel = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]}${multiYear ? ` ${m.slice(2, 4)}` : ''}`;
  const c = d.churn;

  return (
    <>
      {header}
      <div className="bf-kpi-grid">
        <Kpi
          label="MRR · recurrente por mes"
          tone="green"
          icon={<Repeat />}
          value={formatUsd(d.mrrCents)}
          delta={`ARR ${formatUsd(d.arrCents)} · ${d.billedCustomers} clientes`}
          deltaMuted
        />
        <Kpi
          label={`Cobrado · ${range.label}`}
          icon={<CircleDollarSign />}
          value={formatUsd(periodTotal)}
          delta={`${d.periodTotals.newCustomers} clientes nuevos`}
          deltaMuted
        />
        <Kpi
          label={`Churn · ${range.label}`}
          tone={c.rate && c.rate > 0 ? 'amber' : undefined}
          icon={<UserMinus />}
          value={pct(c.rate)}
          delta={`${c.lost} de ${c.base} clientes se fueron`}
          deltaMuted
        />
        <Kpi
          label="Tasa de renovación"
          icon={<TrendingUp />}
          value={pct(d.renewal.rate)}
          delta={`${d.renewal.paid} de ${d.renewal.due} en los últimos 12 meses`}
          deltaMuted
        />
        <Kpi
          label="A cobrar en 90 días"
          tone="amber"
          icon={<CalendarClock />}
          value={formatUsd(d.upcoming.find((u) => u.days === 90)?.amountCents ?? 0)}
          delta={`${d.upcoming.find((u) => u.days === 90)?.count ?? 0} renovaciones`}
          deltaMuted
        />
      </div>

      <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', marginTop: 20 }}>
        <Card>
          <CardHeader title="Licencia vs. abono" subtitle={`Clientes activos, lo que dejan por mes y lo cobrado (${period}).`} />
          <CardBody flush>
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th />
                    {d.byMode.map((m) => (
                      <th key={m.mode} style={{ textAlign: 'right' }}>
                        {BILLING_MODE_LABELS[m.mode]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ['Clientes', (m) => String(m.customers)],
                      ['Altas en el período', (m) => String(m.newCustomers)],
                      ['Recurrente por mes', (m) => formatUsd(m.recurringMonthlyCents)],
                      ['Por cliente, por mes', (m) => formatUsd(m.arpaMonthlyCents)],
                      ['Cobrado en el período', (m) => formatUsd(m.collectedCents)],
                      ['Bajas o atrasados', (m) => String(m.lost)],
                    ] as [string, (m: BillingStatsDTO['byMode'][number]) => string][]
                  ).map(([label, f]) => (
                    <tr key={label}>
                      <td className="bf-cell-sub">{label}</td>
                      {d.byMode.map((m) => (
                        <td key={m.mode} className="bf-num" style={{ textAlign: 'right' }}>
                          {f(m)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Lo recurrente" subtitle="Lo que entra solo, sin vender nada nuevo." />
          <CardBody>
            <Rows
              rows={[
                ['Abonos del sistema (por mes)', formatUsd(d.recurring.subscriptionMonthlyCents)],
                ['bifi (por mes)', formatUsd(d.recurring.bifiMonthlyCents)],
                ['Mantenimientos (por año)', formatUsd(d.recurring.maintenanceYearlyCents)],
                ['MRR · todo llevado a un mes', formatUsd(d.mrrCents)],
                ['ARR · todo llevado a un año', formatUsd(d.arrCents)],
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={`Ingresos por mes · ${range.label}`}
            subtitle="Licencias, mantenimientos, abonos mensuales y lo que suman los clientes actuales."
          />
          <CardBody>
            <Legend />
            <BarChart
              ariaLabel={`Ingresos por mes (${period})`}
              height={200}
              data={d.byMonth.map((m) => ({
                key: m.month,
                label: monthLabel(m.month),
                value: sum(m),
                segments: SERIES.map((x) => ({ value: m[x.key], color: x.color })),
              }))}
              tip={(i) => {
                const m = d.byMonth[i]!;
                return {
                  title: `${monthLabel(m.month)} · ${formatUsd(sum(m))}`,
                  lines: SERIES.filter((x) => m[x.key]).map((x) => `${x.label}: ${formatUsd(m[x.key])}`),
                };
              }}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Por año" />
          <CardBody>
            <Legend />
            <BarChart
              ariaLabel="Ingresos por año"
              height={200}
              data={d.byYear.map((y) => ({
                key: String(y.year),
                label: String(y.year),
                value: sum(y),
                segments: SERIES.map((x) => ({ value: y[x.key], color: x.color })),
              }))}
              tip={(i) => {
                const y = d.byYear[i]!;
                return {
                  title: `${y.year} · ${formatUsd(sum(y))}`,
                  lines: SERIES.filter((x) => y[x.key]).map((x) => `${x.label}: ${formatUsd(y[x.key])}`),
                };
              }}
            />
            <div className="bf-table-wrap" style={{ marginTop: 12 }}>
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Año</th>
                    <th style={{ textAlign: 'right' }}>Licencias</th>
                    <th style={{ textAlign: 'right' }}>Mantenimientos</th>
                    <th style={{ textAlign: 'right' }}>Abonos</th>
                    <th style={{ textAlign: 'right' }}>Clientes actuales</th>
                    <th style={{ textAlign: 'right' }}>Nuevos</th>
                  </tr>
                </thead>
                <tbody>
                  {d.byYear.map((y) => (
                    <tr key={y.year}>
                      <td>{y.year}</td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(y.licenseCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(y.renewalCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(y.subscriptionCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(y.expansionCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {y.newCustomers}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={`Churn · clientes que se van (${period})`}
            subtitle="De los clientes del período (los que había al empezar más los que entraron), cuántos se fueron: dieron de baja el abono, cancelaron o llegaron a la baja por falta de pago. Si vuelven a pagar, dejan de contar."
          />
          <CardBody>
            <Rows
              rows={[
                ['Clientes del período', String(c.base)],
                ['Se fueron', `${c.lost} · ${pct(c.rate)}`],
                ...c.byMode.map((m) => [`${BILLING_MODE_LABELS[m.mode]}`, `${m.lost} de ${m.base} · ${pct(m.rate)}`] as [string, string]),
                ['Lo que dejaban por mes', formatUsd(c.lostMrrCents)],
              ]}
            />
            <h4 style={{ margin: '18px 0 8px' }}>Quiénes se fueron</h4>
            {c.lostList.length === 0 ? (
              <EmptyState icon={<TrendingUp />} title="Nadie se fue en este período" />
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <tbody>
                    {c.lostList.map((x) => (
                      <tr key={x.tenantId}>
                        <td>
                          <span className="bf-cell-main">{x.name}</span>
                          <div className="bf-cell-sub">
                            {PLAN_LABELS[x.plan as Plan] ?? x.plan} · {BILLING_MODE_LABELS[x.mode]}
                          </div>
                        </td>
                        <td className="bf-cell-sub">{x.reason}</td>
                        <td className="bf-cell-sub" style={{ textAlign: 'right' }}>
                          {formatDate(x.at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="En riesgo" subtitle="Todavía no se fueron, pero están atrasados con el pago. Conviene llamarlos." />
          <CardBody flush={c.atRisk.length > 0}>
            {c.atRisk.length === 0 ? (
              <EmptyState icon={<TrendingDown />} title="Nadie atrasado" text="Todos los clientes están al día." />
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <tbody>
                    {c.atRisk.map((x) => (
                      <tr key={x.tenantId}>
                        <td>
                          <span className="bf-cell-main">{x.name}</span>
                          <div className="bf-cell-sub">
                            {PLAN_LABELS[x.plan as Plan] ?? x.plan} · {BILLING_MODE_LABELS[x.mode]}
                          </div>
                        </td>
                        <td>
                          <Badge tone={x.stage === 'READ_ONLY' ? 'danger' : 'warning'}>
                            {x.stage === 'READ_ONLY' ? 'Solo lectura' : 'En gracia'}
                          </Badge>
                        </td>
                        <td className="bf-cell-sub" style={{ textAlign: 'right' }}>
                          Debe desde el {formatDate(x.since)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Renovaciones por cobrar" subtitle="Mantenimientos anuales que vencen (se cobran solos con la tarjeta guardada)." />
          <CardBody>
            <Rows
              rows={d.upcoming.map((u) => [
                `Próximos ${u.days} días`,
                `${formatUsd(u.amountCents)} · ${u.count} ${u.count === 1 ? 'cliente' : 'clientes'}`,
              ])}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Atrasos" subtitle="Clientes que no pagaron su mantenimiento o su abono." />
          <CardBody>
            <Rows
              rows={[
                ['En gracia (15 días con licencia, 7 con abono)', String(d.delinquent.grace)],
                ['Solo lectura', String(d.delinquent.readOnly)],
                ['Dados de baja', String(d.delinquent.suspended)],
                ['Deuda total', formatUsd(d.delinquent.owedCents)],
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Clientes" subtitle={`${d.totalCustomers} en total`} />
          <CardBody>
            <Rows
              rows={[
                ...d.plans.map(
                  (p) => [`Plan ${PLAN_LABELS[p.plan as Plan]}`, `${p.billed} por la web · ${p.manual} cargados a mano`] as [string, string],
                ),
                ['Recurrente por cliente, por año', formatUsd(d.arpaCents)],
                ['Compra promedio en el período', formatUsd(d.avgPurchaseCents)],
                ['Ventas a clientes actuales en el período', formatUsd(d.expansionCents)],
              ]}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Módulos" subtitle="Qué parte de los clientes los tiene." />
          <CardBody>
            {d.modules.map((m) => (
              <div key={m.module} style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{MODULE_LABELS[m.module as ModuleKey]}</span>
                  <strong>
                    {pct(m.pct)} · {m.count}
                  </strong>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--bf-line)' }}>
                  <div style={{ width: `${m.pct * 100}%`, height: '100%', borderRadius: 4, background: 'var(--bf-celeste, #2E9BDA)' }} />
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
      {SERIES.map((s) => (
        <span key={s.key} className="bf-cell-sub" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} aria-hidden />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function Rows({ rows }: { rows: [string, string][] }) {
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px 16px', margin: 0 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt className="bf-cell-sub">{k}</dt>
          <dd style={{ margin: 0, fontWeight: 600, textAlign: 'right' }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
